"use client";
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from "react";
import { Icon } from "@iconify/react";
import { useAuth } from "@/contexts/authContext";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/custom/ui/tooltip";
import { Menu } from "@headlessui/react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from "@tanstack/react-table";
import { supabase } from "@/supabase/client";
import { Button } from "../../ui/button";
import { ScrollArea, ScrollBar } from "../../ui/scroll-area";
import DataTablePagination from "../components/pagination";
import DataTableViewOptions from "../components/view-options";
import DataTableSearch from "../components/search";
import DataTableFacetedFilter from "../components/faceted-filter";
import DetailsModal from "./details-modal";

const SeasonsTableServer = forwardRef(
  (
    {
      payload,
      columnDef,
      colSearch,
      facetedFilterRef,
      facetedFilterLists,
      showOptions = false,
      selectedSeasonData,
    },
    ref
  ) => {
    const defaultData = useMemo(() => [], []);
    const [pagination, setPagination] = useState({
      pageIndex: 0,
      pageSize: 10,
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalData, setModalData] = useState({ type: "", data: [] });
    const { user: givenUser, walletAddress } = useAuth();

    // State to hold the fetched data and total row count
    const [tableData, setTableData] = useState(defaultData);
    const [rowCount, setRowCount] = useState(0);

    // Function to extract field names from the fetched data
    const extractFieldNames = (data) => {
      if (data.length > 0) {
        return Object.keys(data[0]);
      }
      return [];
    };

    // New function to fetch data directly from Supabase
    useEffect(() => {
      const fetchData = async () => {
        // Determine which table to use based on selectedSeasonData
        let tableName = "seasons_diff";
        if (selectedSeasonData && !selectedSeasonData.is_active) {
          tableName = `seasons_final_diff_snapshot_${selectedSeasonData.id}`;
        }

        // console.log(tableName);

        // Fetch page data and total count
        const { data, error, count } = await supabase
          .from(tableName)
          .select("*", { count: "exact" })
          .order("mxs_rank", { ascending: true })
          .range(
            pagination.pageIndex * pagination.pageSize,
            (pagination.pageIndex + 1) * pagination.pageSize - 1
          );

        if (error) {
          console.error("Error fetching data:", error);
          return;
        }

        // console.log(data);

        let combinedData = data;

        // Fetch current user data only if walletAddress is available
        if (walletAddress) {
          const { data: currentUserData, error: currentUserError } =
            await supabase
              .from(tableName)
              .select("*")
              .eq("wallet_lc", walletAddress.toLowerCase())
              .single();

          if (currentUserError) {
            console.error(
              "Error fetching current user data (season table):",
              currentUserError
            );
          } else if (currentUserData) {
            combinedData = [currentUserData, ...data];
          }
        }

        // Fetch images
        const dataWithPhotos = await fetchPhotoUrls(combinedData);
        // console.log(dataWithPhotos[0]);

        // Filter and update data
        const filterFields = (item) => {
          const {
            id,
            created_at,
            wallet_lc,
            wallet,
            user_id,
            updated_at,
            ...filteredItem
          } = item;
          return { ...filteredItem, name: item.name };
        };

        const filteredData = dataWithPhotos.map(filterFields);
        setTableData(filteredData);
        setRowCount(count || data.length);
      };

      fetchData();
    }, [pagination, walletAddress, selectedSeasonData]);

    // Fetch photo_url from the users table using wallet_lc
    const fetchPhotoUrls = async (data) => {
      return await Promise.all(
        data.map(async (row) => {
          const { data: userData, error } = await supabase
            .from("users_duplicate")
            .select("photo_url")
            .eq("wallet_lc", row.wallet_lc)
            .single();

          if (error) {
            console.error("Error fetching photo_url:", error);
          }

          return {
            ...row,
            photo_url:
              userData?.photo_url ||
              "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/branding%2Fmxs_games%2Fv1%2Fwhite%2Fpng%2Fmxs_icon_white.png?alt=media&token=8a7e36ba-954b-46b3-bcb2-559cec885bdf",
          };
        })
      );
    };

    // Ensure 'photo_url' and 'title' are not included in the field names
    const originalFieldNames = extractFieldNames(tableData).filter(
      (fieldName) => fieldName !== "photo_url" && fieldName !== "title"
    );

    // Prioritize these fields by moving them to the beginning of the list
    const orderedFields = [
      "mxs_rank",
      "name",
      "mxs",
      "gold",
      "ruby",
      "sapphire",
      "level_count",
      "missions_count",
      "gold_contribution",
      "ruby_contribution",
      "sapphire_contribution",
      // "login_count",
      // "ads_count",
    ];

    // Prepend the ordered fields to the beginning of the list
    orderedFields.forEach((field) => {
      const index = originalFieldNames.indexOf(field);
      if (index > -1) {
        originalFieldNames.splice(index, 1);
      }
    });

    // Prepend the ordered fields to the beginning of the list
    originalFieldNames.unshift(...orderedFields);

    const fieldNames = originalFieldNames.map((fieldName) => {
      if (fieldName.length <= 3) {
        return fieldName.toUpperCase();
      }
      // Capitalize the first letter and remove underscores
      return fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    });

    // Compute total pages for manual pagination
    const pageCount = Math.ceil(rowCount / pagination.pageSize);

    // Update the table data with manual pagination
    const table = useReactTable({
      data: tableData,
      columns: columnDef,
      manualPagination: true,
      pageCount,
      state: { pagination },
      onPaginationChange: setPagination,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    const handleReload = async () => {
      // Commenting out the existing useQuery hook
      // await dataQuery.refetch();
    };

    useImperativeHandle(ref, () => ({
      handleReload,
    }));

    // Function to format numbers
    const formatNumber = (value, fieldName) => {
      if (typeof value === "number") {
        if (
          [
            "gold_contribution",
            "ruby_contribution",
            "sapphire_contribution",
          ].includes(fieldName)
        ) {
          return value.toFixed(1);
        }
        return Math.ceil(value);
      }
      return value;
    };

    // Enhanced function to get tooltip text based on field name
    const getTooltipText = (fieldName) => {
      // console.log(fieldName);
      const tooltips = [
        {
          title: "mxs",
          tooltip:
            "MXS points are accumulated by playing games on the MXS platform.",
        },
        {
          title: "level_count",
          tooltip: "LEVEL COUNT is the number of levels you have completed.",
        },
        {
          title: "missions_count",
          tooltip:
            "MISSIONS COUNT is the number of missions you have completed.",
        },
        {
          title: "login_count",
          tooltip:
            "LOGIN COUNT is the number of times you have logged in to the MXS platform.",
        },
        {
          title: "ads_count",
          tooltip: "ADS COUNT is the number of ads you have watched.",
        },
        {
          title: "user_referral_count",
          tooltip:
            "USER REFERRAL COUNT is the number of users you have referred.",
        },
        {
          title: "products_referral",
          tooltip:
            "PRODUCTS REFERRAL is the number of products you have referred.",
        },
        {
          title: "nft_referral_count",
          tooltip:
            "NFT REFERRAL COUNT is the number of nfts you have referred.",
        },
        {
          title: "nft_count",
          tooltip: "NFT COUNT is the number of NFTs you have in your wallet.",
        },
        { title: "nft_power", tooltip: "NFT POWER is the power of your NFTs." },
        {
          title: "xb",
          tooltip: "XSEED BALANCE is the balance of XSEED in your wallet.",
        },
        {
          title: "ruby_contribution",
          tooltip:
            "RUBY CONTRIBUTION measures your total contributions to the Ruby ecosystem within the platform.",
        },
        {
          title: "gold_contribution",
          tooltip:
            "GOLD CONTRIBUTION tracks your overall contributions to the Gold economy in the game.",
        },
        {
          title: "sapphire_contribution",
          tooltip:
            "SAPPHIRE CONTRIBUTION represents your total contributions to the Sapphire network.",
        },
        {
          title: "ruby_rank",
          tooltip:
            "RUBY RANK shows your standing among all players based on Ruby-related achievements.",
        },
        {
          title: "gold_rank",
          tooltip:
            "GOLD RANK indicates your position in the leaderboard for Gold-related activities.",
        },
        {
          title: "sapphire_rank",
          tooltip:
            "SAPPHIRE RANK displays your competitive ranking in Sapphire-based challenges.",
        },
      ];

      // Convert fieldName to match the format in tooltips
      const formattedFieldName = fieldName.toLowerCase().replace(/ /g, "_");

      const tooltipItem = tooltips.find(
        (item) => item.title === formattedFieldName
      );
      return tooltipItem ? tooltipItem.tooltip : null;
    };

    // Function to format field names
    const formatFieldName = (fieldName) => {
      if (fieldName.length <= 3) {
        return fieldName.toUpperCase();
      }
      // Capitalize the first letter and remove underscores
      return fieldName
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    // State to manage visible columns
    const [visibleColumns, setVisibleColumns] = useState(originalFieldNames);

    // Memoize the filtered columns for rendering efficiency
    const displayedColumns = useMemo(() => {
      // console.log("🔄 Recomputing displayedColumns..."); // Debug log

      const filteredColumns = originalFieldNames.filter((fieldName) => {
        const isVisible = visibleColumns.includes(fieldName);
        // console.log(
        //   `🔎 Checking "${fieldName}": ${
        //     isVisible ? "✅ Visible" : "❌ Hidden"
        //   }`
        // ); // Debug log
        return isVisible;
      });

      // console.log("📋 Final displayedColumns:", filteredColumns); // Debug log
      return filteredColumns;

      // 🧩 Memoization dependencies:
      // - visibleColumns ➡️ Recompute when visibility changes
      // - originalFieldNames ➡️ Recompute if source fields change
    }, [visibleColumns, originalFieldNames]);

    // const displayedColumns = originalFieldNames.slice(0, 10)

    // console.log(originalFieldNames);
    // console.log(originalFieldNames.slice(0, 10));
    // console.log(visibleColumns);
    // console.log(displayedColumns);

    // Function to handle column visibility toggle
    const handleColumnToggle = (fieldName) => {
      setVisibleColumns((prev) => {
        if (prev.includes(fieldName)) {
          return prev.filter((col) => col !== fieldName);
        } else {
          return [...prev, fieldName];
        }
      });
    };

    // State to manage the visibility of the column selector
    const [isSelectorVisible, setIsSelectorVisible] = useState(false);

    // Function to toggle the column selector visibility
    const toggleSelectorVisibility = () => {
      setIsSelectorVisible((prev) => !prev);
    };

    // Button to toggle column selector using Headless UI
    const ColumnSelectorToggle = () => (
      <Menu as="div" className="relative inline-block text-left">
        <div className="flex items-center justify-between mb-4 w-full">
          <Menu.Button className="inline-flex items-center gap-2 px-3 py-1.5 rounded border text-zinc-400 border-zinc-600 text-sm">
            <Icon icon="mdi:eye" className="mr-2" />
            <span>Show/Hide Columns</span>
          </Menu.Button>
        </div>
        <Menu.Items
          transition
          anchor="bottom end"
          className="absolute w-56 right-0 z-10 bg-zinc-800 shadow-md rounded p-2 text-white mt-2 border border-gray-500"
        >
          {originalFieldNames.slice(6).map((fieldName, index) => (
            <Menu.Item key={fieldName}>
              {({ active }) => (
                <div
                  className={`flex items-center ${active ? "bg-zinc-700" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(fieldName)}
                    onChange={() => handleColumnToggle(fieldName)}
                    className="form-checkbox text-blue-500 bg-zinc-700 border-zinc-600 focus:ring-0"
                  />
                  <label className="ml-2">{formatFieldName(fieldName)}</label>
                </div>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>
    );

    return (
      <>
        <div className={`grid gap-4`}>
          {showOptions && (
            <div className={`flex items-center justify-between`}>
              <div className="flex flex-1 items-center space-x-2">
                {colSearch && (
                  <DataTableSearch table={table} column={colSearch} />
                )}
                {facetedFilterRef && (
                  <DataTableFacetedFilter
                    table={table}
                    column={table.getColumn(facetedFilterRef)}
                    title={
                      facetedFilterRef.charAt(0).toUpperCase() +
                      facetedFilterRef.slice(1)
                    }
                    options={facetedFilterLists}
                  />
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" onClick={handleReload}>
                  <span>Reload</span>
                  {/* Commenting out the existing loading indicator */}
                  {/* {isLoading && <Loader2 className="ml-2 h-4 w-4 animate-spin" />} */}
                </Button>
                <DataTableViewOptions table={table} />
              </div>
            </div>
          )}
          <div className="relative flex items-center justify-between">
            <div className="text-2xl font-bold text-white flex items-center gap-2">
              <Icon icon="tdesign:leaderboard-filled" className="h-6 w-6" />
              Leaderboard
            </div>

            <ColumnSelectorToggle />
          </div>
          <div className={`rounded-md border border-[#333] overflow-x-auto`}>
            <ScrollArea>
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-metaxseed-blue to-metaxseed-open-blue/80">
                    <th
                      colSpan="2"
                      className="py-2 px-3 text-center text-white text-xs gothamMedium border-r-2 border-gray-700"
                    >
                      Details
                    </th>
                    <th
                      colSpan="4"
                      className="py-2 px-3 text-center text-white text-xs gothamMedium border-r-2 border-gray-700"
                    >
                      Points
                    </th>
                    <th
                      colSpan={`${displayedColumns.length - 4}`}
                      className="py-2 px-3 text-center text-white text-xs gothamMedium"
                    >
                      Engagement
                    </th>
                  </tr>
                  <tr className="bg-metaxseed-blue/30">
                    {displayedColumns.map((fieldName, index) => {
                      const isLastInGroup = index === 1 || index === 5;
                      const isTypeField = [
                        "RUBY",
                        "GOLD",
                        "SAPPHIRE",
                        "MXS",
                      ].includes(fieldName.toUpperCase());
                      return (
                        <th
                          key={index}
                          className={`py-2 px-3 text-left text-white text-xs gothamMedium border-r border-gray-700 ${
                            isLastInGroup ? "border-r-2 border-gray-700" : ""
                          }`}
                          style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                          }}
                        >
                          <span className="flex items-center mb-2">
                            {isTypeField ? (
                              <div className="flex items-center">
                                <Icon
                                  icon="mdi:information-outline"
                                  className="mr-1 mb-2 cursor-pointer text-lg transform rotate-90"
                                  onClick={() => {
                                    setModalData({
                                      type: fieldName.toLowerCase(),
                                      data: [],
                                    });
                                    setIsModalOpen(true);
                                  }}
                                />
                              </div>
                            ) : index > 1 ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Icon
                                      icon="mdi:help-circle-outline"
                                      className="mr-1 mb-2 cursor-pointer transform rotate-90 text-lg"
                                      title={`Information about ${fieldName}`}
                                    />
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-metaxseed-blue/80 text-white">
                                    <p>{getTooltipText(fieldName)}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                            {formatFieldName(fieldName)}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`border-t border-zinc-700 hover:bg-[#1f1f1f] transition ${
                        walletAddress && rowIndex === 0
                          ? "bg-gradient-to-r from-orange-600 to-yellow-600"
                          : ""
                      }`}
                    >
                      {displayedColumns.map(
                        (fieldName, index) =>
                          fieldName !== "photo_url" && (
                            <td
                              key={index}
                              className={`py-2 px-3 text-left border-r border-zinc-700 text-gray-300 text-xs ${
                                index === 1 || index === 5
                                  ? "border-r-2 border-zinc-700"
                                  : ""
                              } ${index === 1 ? "pr-8" : ""} ${
                                index === 0 ? "font-bold" : ""
                              }`}
                            >
                              <div
                                className={`${
                                  index >= 3 && index <= 5
                                    ? "rounded p-1 w-full flex justify-center font-semibold bg-metaxseed-content-gray"
                                    : ""
                                } ${
                                  index === 2
                                    ? "rounded p-1 w-full flex justify-center font-semibold bg-metaxseed-blue"
                                    : ""
                                }`}
                              >
                                {fieldName === "name" ? (
                                  <div className="flex items-center">
                                    <img
                                      src={row.photo_url}
                                      alt="User Photo"
                                      className="w-6 h-6 rounded-full mr-2"
                                    />
                                    {row[fieldName]}
                                  </div>
                                ) : (
                                  formatNumber(row[fieldName], fieldName)
                                )}
                              </div>
                            </td>
                          )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
          {/* <DataTableViewOptions table={table} /> */}
          <DataTablePagination table={table} totalDocs={tableData.length} />
        </div>
        <DetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          data={modalData}
        />
      </>
    );
  }
);

SeasonsTableServer.displayName = "SeasonsTableServer";

export default SeasonsTableServer;
